import {
  AutoStack,
  DayPickerStrings,
  FormikGraphQLDatePicker,
  FormikProvingGroundDropdown,
  onFormatDate,
  OrderIdPicker,
  Text,
} from 'components'
import { FormikMultiUserObjectPicker } from 'components/PersonPicker'
import { NotificationDialog } from 'dialogs/NotificationDialog'
import {
  FastField,
  Field,
  FieldArray,
  FormikConsumer,
  FormikContext,
} from 'formik'
import {
  FormikDropdown,
  FormikSpinButton,
  FormikTextField,
} from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack, Stack } from 'forms/FormStack'
import { Box } from 'grid'
import { getDropdownOptionsForEnum } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useDialogState } from 'hooks/useDialogState'
import { useGlobalSettings } from 'hooks/useGlobalSettings'
import {
  ActiveProvingTypesSetting,
  GlobalSettingType,
  ProvingInsertType,
  ProvingType,
  UserRole,
} from 'model'
import { DayOfWeek, IconButton, Label } from 'office-ui-fabric-react'
import { Position } from 'office-ui-fabric-react/lib/utilities/positioning'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useReadProvingGroundsQuery } from 'views/AdminProvingGroundsView/AdminProvingGroundsView.generated'
import * as Yup from 'yup'

export type ProvingGroundFormValues = ProvingInsertType

const summErrorMessage: string = 'Die Summe der Prozentangabe sollte genau 100%'

export class CreateProvingFormSchema {
  constructor() {
    return Yup.object().shape({
      type: Yup.string()
        .required('Art der Erprobung ist ein Pflichtfeld')
        .notOneOf([''], 'Art der Erprobung darf nicht leer sein'),
      provingGroundId: Yup.string().required('Standort ist ein Pflichtfeld'),
      title: Yup.string().trim().required('Titel ist ein Pflichtfeld'),
      description: Yup.string().trim().required(),
      department: Yup.string().trim().required(),
      dateFrom: Yup.date().required('Datum nicht valide'),
      dateTo: Yup.date()
        .required('Datum nicht valide')
        .when(
          'dateFrom',
          (dateFrom: any, schema: any) =>
            dateFrom && schema.min(dateFrom, 'Datum invalide')
        ),
      responsible: Yup.array()
        .of(Yup.object().required())
        .min(1, 'Mindestens ein Verantwortlicher muss ausgewählt werden')
        .required('Verantwortlicher ist ein Pflichtfeld'),
      orderIdDistribution: Yup.array()
        .of(
          Yup.object().shape({
            orderId: Yup.string().required(),
            percentage: Yup.number().required(),
          })
        )
        .required(),
      // .test('sum', summErrorMessage, orders => {
      //   let sum = 0
      //   orders.forEach((element: { percentage: number; orderId: string }) => {
      //     sum += element.percentage
      //   })
      //   const isValid = sum <= 100 ? true : false
      //   return isValid
      // }),
    })
  }
}

export const CreateProvingFormWithContext: React.FunctionComponent<
  FormikContext<ProvingGroundFormValues>
> = (ctx) => {
  const { t } = useTranslation()
  const [orderIdsValid, setOrderIdsValid] = useState(true)
  useEffect(() => {
    ctx.setSubmitting(!orderIdsValid)
  }, [orderIdsValid])
  const auth = useAuthContext()
  const [noteState, setNoteState] = React.useState<string | null>(null)
  const [dialogState, showDialog] = useDialogState()
  const provingGrounds = useReadProvingGroundsQuery()
  const isAdmin = auth.hasUserRole(UserRole.SystemAdministrator)
  const currentDate = new Date()
  const minDate = isAdmin ? {} : { minDate: currentDate }
  const { getSetting } = useGlobalSettings()
  const provingTypesSetting = getSetting(
    GlobalSettingType.ActiveProvingTypes
  ) as ActiveProvingTypesSetting

  const provingTypes = provingTypesSetting
    ? provingTypesSetting.typeSettings
        .filter((ts) => ts.isActive)
        .map((ts) => ts.type)
    : []

  const provingTypeOptions = [
    ...(provingTypes
      ? getDropdownOptionsForEnum<ProvingType>(
          t,
          ProvingType,
          'provingType'
        ).filter((option) => provingTypes.includes(option.key as ProvingType))
      : []),
  ]

  return (
    <FormStack>
      <FieldStack>
        <FieldStackItem
          name="type"
          render={(fieldProps) => (
            <>
              <Label required={true}>{t('field.provingType.label')}</Label>
              <FormikDropdown options={provingTypeOptions} {...fieldProps} />
            </>
          )}
        />
        <FieldStackItem
          name="provingGroundId"
          label={t('field.provingGround.label')}
          required={true}
          component={FormikProvingGroundDropdown}
          emptyOption={true}
          onChanged={(p: any) => {
            const pg = p.key
              ? provingGrounds &&
                provingGrounds.data &&
                provingGrounds.data.provingGrounds &&
                provingGrounds.data.provingGrounds.find(
                  (pgs: any) => pgs._id === p.key
                )
              : undefined
            const note = pg && pg.specialNote ? pg.specialNote : ''
            if (note) {
              setNoteState(note)
              showDialog()
            }
          }}
        />
        <FieldStackItem
          name="title"
          required={true}
          label={t('field.title.label')}
          component={FormikTextField}
        />
        <FieldStackItem
          name="description"
          placeholder={t('field.description.placeholder')}
          label={t('field.description.label')}
          multiline={true}
          required={true}
          component={FormikTextField}
        />
      </FieldStack>
      <Stack.Item>
        <Text font="xLarge" mt={2} mb={1}>
          {t('field.timespan.label')}
        </Text>
      </Stack.Item>
      <FieldStack horizontal={true}>
        <FieldStackItem
          name="dateFrom"
          isRequired={true}
          component={FormikGraphQLDatePicker}
          firstDayOfWeek={DayOfWeek.Monday}
          strings={DayPickerStrings}
          showWeekNumbers={true}
          {...minDate}
          formatDate={onFormatDate}
          label={t('field.dateFrom.label')}
        />
        <FieldStackItem
          name="dateTo"
          isRequired={true}
          render={(fieldProps) => {
            return (
              <>
                <Label required={true}>{t('field.dateTo.label')}</Label>
                <FormikGraphQLDatePicker
                  {...fieldProps}
                  minDate={
                    fieldProps.form.values.dateFrom
                      ? new Date(fieldProps.form.values.dateFrom)
                      : undefined
                  }
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  formatDate={onFormatDate as any}
                  initialPickerDate={
                    fieldProps.form.values.dateFrom
                      ? new Date(fieldProps.form.values.dateFrom)
                      : undefined
                  }
                  disabled={!fieldProps.form.values.dateFrom}
                />
              </>
            )
          }}
        />
      </FieldStack>
      <Stack.Item>
        <Text font="xLarge" mt={2} mb={1}>
          {t('section.applicant.header')}
        </Text>
      </Stack.Item>
      <FieldStack>
        <Field
          name="department"
          label={t('section.applicant.field.department')}
          required={true}
          component={FormikTextField}
          style={{ textTransform: 'uppercase' }}
        />
        <Stack.Item>
          <Label required={true}>
            {t('section.applicant.field.responsible')}
          </Label>
          <Field
            name="responsible"
            required={true}
            label="test"
            itemLimit={1}
            component={FormikMultiUserObjectPicker}
          />
        </Stack.Item>
      </FieldStack>
      <Stack.Item>
        <Text font="xLarge" mt={2} mb={1}>
          {t('section.orderIds.header')}
        </Text>
      </Stack.Item>
      <FieldArray name="orderIdDistribution">
        {({ form, name, ...helpers }) => {
          const values: { orderId: string; percentage: number }[] =
            form.values[name] || []

          return (
            <AutoStack tokens={{ childrenGap: 4 }}>
              {values.map((x: any, i: any) => (
                <AutoStack
                  horizontal={true}
                  verticalAlign="center"
                  tokens={{ childrenGap: 8 }}
                  key={i}
                >
                  <FastField
                    name={`orderIdDistribution[${i}].orderId`}
                    render={(props: any) => (
                      <FormikTextField
                        readOnly={true}
                        styles={{ field: { width: 178 } }}
                        {...props}
                      />
                    )}
                  />
                  <FastField
                    name={`orderIdDistribution[${i}].percentage`}
                    validate={() => {
                      if (i !== 0) {
                        return undefined
                      }
                      if (Array.isArray(form.touched.orderIdDistribution)) {
                        const sum =
                          form.values.orderIdDistribution &&
                          form.values.orderIdDistribution.reduce(
                            (summ: number, orderObj: any) =>
                              summ + orderObj!.percentage!,
                            0
                          )

                        if (sum !== undefined && sum !== 100) {
                          setOrderIdsValid(false)
                          return undefined
                        } else {
                          setOrderIdsValid(true)
                          return undefined
                        }
                      } else {
                        setOrderIdsValid(true)
                        return undefined
                      }
                    }}
                    render={(props: any) => (
                      <FormikSpinButton
                        styles={{
                          input: { width: 40 },
                          label: { marginLeft: -7 },
                        }}
                        min={0}
                        label="%"
                        labelPosition={Position.end}
                        max={100}
                        onValidate={(value: string) => {
                          if (
                            Number(value) > 100 ||
                            Number(value) < 0 ||
                            value.trim().length === 0 ||
                            isNaN(+value)
                          ) {
                            if (
                              props.form.initialValues.orderIdDistribution &&
                              props.form.initialValues.orderIdDistribution[i]
                            ) {
                              return Number(
                                props.form.initialValues.orderIdDistribution[i]
                                  .percentage
                              )
                            } else {
                              return 0
                            }
                          } else {
                            return Number(value)
                          }
                        }}
                        step={5}
                        {...props}
                      />
                    )}
                  />
                  <Stack.Item grow={1}>
                    <FastField
                      name={`orderIdDistribution[${i}].annotation`}
                      render={(props: any) => (
                        <FormikTextField
                          placeholder={t('section.orderIds.field.term')}
                          styles={{ fieldGroup: { flex: '1' } }}
                          {...props}
                        />
                      )}
                    />
                  </Stack.Item>
                  <IconButton
                    styles={{ root: { color: 'red' } }}
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => {
                      helpers.remove(i)
                    }}
                  />
                </AutoStack>
              ))}
              <OrderIdPicker
                blacklist={values.map((v) => v.orderId)}
                styles={{ text: { width: 175 } }}
                inputProps={{
                  placeholder: '+ ' + t('section.orderIds.field.orderId'),
                }}
                itemLimit={1}
                onItemSelected={(item) => {
                  if (item) {
                    helpers.push({
                      orderId: item.key,
                      percentage:
                        100 - values.reduce((sum, x) => sum + x.percentage, 0),
                      annotation: '',
                    })
                    return null
                  }
                  return null
                }}
              />

              <AutoStack
                horizontal={true}
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
              >
                <Box width="175px" style={{ textAlign: 'right' }} px={1}>
                  {t('section.orderIds.field.sum')}
                </Box>
                <Box px={1}>
                  {values.reduce((sum, x) => sum + x.percentage || 0, 0)}%
                </Box>
              </AutoStack>
              <div style={{ color: 'red' }}>
                {form.errors.orderIdDistribution}
              </div>
              {!orderIdsValid && (
                <div style={{ color: 'red' }}>{summErrorMessage}</div>
              )}
            </AutoStack>
          )
        }}
      </FieldArray>
      {noteState && (
        <NotificationDialog
          {...dialogState}
          titleText={'Standorthinweis'}
          subText={noteState}
          richText={true}
        />
      )}
    </FormStack>
  )
}

export const CreateProvingForm: React.FunctionComponent = () => (
  <FormikConsumer>
    {(ctx) => <CreateProvingFormWithContext {...ctx} />}
  </FormikConsumer>
)
